import { orgAPI } from "@/api/api";
import { fileRecordKeys } from "@/api/fileRecords";
import { SELF_CANVAS_LINK_REASON } from "@/dnd/classifyTransfer";
import { useCanvas } from "@/pages/canvas/context/CanvasContext";
import { useCanvasRoute } from "@/pages/canvas/context/CanvasRouteContext";
import { buildCanvasLinkEntity } from "@/pages/canvas/utils/canvasLinkEntity";
import {
  buildFileRecordEntityForCanvas,
  defaultFileRecordNodeBox,
} from "@/pages/canvas/utils/imageDimensions";
import { parseAppCanvasUrl } from "@/public-canvas/appCanvasUrl";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";

export interface CreateFromURLArgs {
  sourceUrl: string;
  /** Canvas-space coordinates at which to place the loading node. */
  position: { x: number; y: number };
}

/**
 * Normalizes a user-supplied URL string. Prepends `https://` when the input
 * has no scheme (e.g. "cohesive.ai" → "https://cohesive.ai"). Returns the
 * input unchanged when it already starts with http:// or https://.
 */
export function normalizeSourceUrl(raw: string): string {
  const trimmed = raw.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/**
 * Derives a human-readable label from a URL for use as the loading node
 * title before the server scrapes the page title. Mirrors the Go-side
 * `URLLeafToPath` heuristic: last non-empty path segment, falling back
 * to the hostname.
 */
function urlDisplayName(rawURL: string): string {
  try {
    const u = new URL(rawURL);
    const segments = u.pathname.split("/").filter(Boolean);
    const leaf = segments[segments.length - 1];
    if (leaf) {
      // Strip extension for display (e.g. "report.pdf" → "report")
      return leaf.replace(/\.[^.]+$/, "") || leaf;
    }
    return u.hostname;
  } catch {
    return rawURL;
  }
}

/**
 * Derives the `destination.path` sent to the server — a provisional leaf
 * name the server can use as a fallback if scraping produces no title.
 * Always ends in `.md` because the manager defaults to Markdown for URLs
 * that can't be classified until after scraping.
 */
function urlToProvisionalPath(rawURL: string): string {
  try {
    const u = new URL(rawURL);
    const segments = u.pathname.split("/").filter(Boolean);
    const leaf = segments[segments.length - 1];
    if (leaf) {
      // Strip any existing extension and re-attach .md
      const base = leaf.replace(/\.[^.]+$/, "") || leaf;
      return `${base}.md`;
    }
    // Return the hostname bare — the backend's derivePathFromURL will strip
    // the TLD-as-extension (e.g. ".ai", ".io") and reapply the canonical one.
    // Appending ".md" here would produce double-extensions like "cohesive.ai.md".
    return u.hostname;
  } catch {
    return "resource.md";
  }
}

/**
 * Optimistic URL-ingestion hook for the canvas.
 *
 * Mints a loading ghost node at `position`, POSTs `source_url` to the file
 * records API, then swaps the ghost for the real entity when the server
 * responds. Mirrors the upload-queue ghost pattern used for file drops.
 *
 * Usage:
 * ```ts
 * const ingestURL = useFileRecordCreateFromURL();
 * ingestURL({ sourceUrl: "https://example.com", position: { x: 100, y: 200 } });
 * ```
 */
export function useFileRecordCreateFromURL() {
  const { canvasId } = useCanvasRoute();
  const { addNode, replaceNodeData, updateNodeDimensions } = useCanvas();
  const queryClient = useQueryClient();

  return useCallback(
    ({ sourceUrl: rawSourceUrl, position }: CreateFromURLArgs) => {
      if (!canvasId) return;

      const sourceUrl = normalizeSourceUrl(rawSourceUrl);

      // A link to one of our own canvases is a canvas card, not a web page
      // to scrape. Reference-only; the host canvas can't link to itself.
      const linkedCanvasId = parseAppCanvasUrl(sourceUrl);
      if (linkedCanvasId) {
        if (linkedCanvasId === canvasId) {
          toast.error(SELF_CANVAS_LINK_REASON);
          return;
        }
        orgAPI.canvas
          .getById(linkedCanvasId)
          .then((canvas) => {
            addNode({
              id: uuidv4(),
              type: "entity",
              position,
              data: { entity: buildCanvasLinkEntity(canvas) },
            } as Parameters<typeof addNode>[0]);
          })
          .catch(() => {
            toast.error("Couldn't find that canvas");
          });
        return;
      }

      const nodeId = uuidv4();
      const displayTitle = urlDisplayName(sourceUrl);

      // Place a loading ghost immediately so the user gets instant feedback.
      // The content type is unknown until the server fetches the URL, and
      // unknown is a page; image URLs are re-fitted below once the record
      // arrives with its dimensions.
      const ghostBox = defaultFileRecordNodeBox();
      addNode({
        id: nodeId,
        type: "entity",
        position,
        width: ghostBox.width,
        height: ghostBox.height,
        data: {
          loading: true,
          entity: {
            type: "file_record",
            base: "file_record",
            platform: "cohesive",
            data: { id: "", title: displayTitle },
          },
        },
      } as Parameters<typeof addNode>[0]);

      orgAPI.fileRecords
        .create({
          provider: "s3",
          source_url: sourceUrl,
          destination: {
            class: "canvas",
            canvas_id: canvasId,
            path: urlToProvisionalPath(sourceUrl),
            is_folder: false,
          },
        })
        .then((record) => {
          // Pre-seed per-id cache so detail pane loads without a round-trip.
          queryClient.setQueryData(fileRecordKeys.detail(record.id), record);
          queryClient.setQueryData(
            fileRecordKeys.canvas(canvasId),
            (prev: (typeof record)[] | undefined) => {
              if (!prev) return [record];
              const idx = prev.findIndex((r) => r.id === record.id);
              if (idx === -1) return [...prev, record];
              const next = prev.slice();
              next[idx] = { ...prev[idx], ...record };
              return next;
            },
          );

          const { entity, nodeWidth, nodeHeight } =
            buildFileRecordEntityForCanvas({ record });
          replaceNodeData(nodeId, { entity });
          // For image URLs the backend decodes natural dimensions and returns
          // them in record.width/height. Resize the ghost node to the fitted
          // aspect so image tiles don't stay at the generic file-card box.
          updateNodeDimensions(nodeId, {
            width: nodeWidth,
            height: nodeHeight,
          });
        })
        .catch((err: unknown) => {
          const serverMessage = (err as any)?.response?.data?.error?.message;
          const errorMessage =
            typeof serverMessage === "string"
              ? serverMessage
              : "Something went wrong. Please try again.";
          // Keep the node on canvas in an error state so the user can see
          // what failed and still access the original URL.
          replaceNodeData(nodeId, {
            loading: false,
            error: {
              title: "Failed to load URL",
              message: errorMessage,
              sourceUrl: sourceUrl,
            },
            entity: {
              type: "file_record",
              base: "file_record",
              platform: "cohesive",
              data: { id: "", title: displayTitle },
            },
          });
        });
    },
    [canvasId, addNode, replaceNodeData, updateNodeDimensions, queryClient],
  );
}
